import * as Yup from 'yup';
import { useSelector, useDispatch } from 'react-redux';
import { Link, Redirect } from 'react-router-dom';
import { Form, Formik } from 'formik';
import { makeStyles } from '@material-ui/core/styles';
import Box from '@material-ui/core/Box';
import Typography from '@material-ui/core/Typography';
import Button from '@material-ui/core/Button';
import Container from '@material-ui/core/Container';
import { Grid } from '@material-ui/core';
import { updateAddress, updateCustomer } from 'store/actions/cart';
import { Alert } from '@material-ui/lab';
import { useAuth } from 'contexts/AuthContext';
import { useCustomerFormPopulation } from 'hooks/useCustomerFormPopulation';
import CustomerInfoForm from './CustomerInfoForm';
import DropshipAddressForm from './DropshipAddressForm';
import {
  BUTTON_STYLES,
  GRID_SIZES,
  DEFAULT_COUNTRY,
} from 'constants/addressForm';

const validateSchema = Yup.object().shape({
  customer: Yup.object().shape({
    email: Yup.string()
      .email('Please enter a valid email address')
      .required('Required'),
  }),
});

const useStyles = makeStyles((theme) => ({
  root: {
    backgroundColor: theme.palette.background.paper,
  },
  btnxl: BUTTON_STYLES.btnxl,
}));

// Separate component to handle form population
const AddressForm = ({
  isAuthenticated,
  isDropship,
  isPickup,
  shipping_countries,
  onSubmit,
  shippingAddress,
}) => {
  const classes = useStyles();
  const { isProfileComplete } = useAuth();
  const { globalOptions } = useSelector((s) => s.lab);

  return (
    <Formik
      validationSchema={validateSchema}
      initialValues={{
        customer: {
          firstName: '',
          lastName: '',
          businessName: '',
          phone: '',
          email: '',
          address1: '',
          address2: '',
          city: '',
          country:
            shipping_countries.length > 1
              ? ''
              : shipping_countries[0].country.code,
          userId: '',
          state: '',
          zip: '',
        },
        shippingAddress: {
          name: shippingAddress?.name || '',
          businessName: shippingAddress?.businessName || '',
          address1: shippingAddress?.address1 || '',
          address2: shippingAddress?.address2 || '',
          city: shippingAddress?.city || '',
          country: shippingAddress?.country
            ? shippingAddress.country
            : shipping_countries.length > 1
            ? ''
            : shipping_countries[0].country.code,
          state: shippingAddress?.state || '',
          zip: shippingAddress?.zip || '',
        },
      }}
      onSubmit={onSubmit}
    >
      {({ values, handleSubmit, setFieldValue }) => {
        const shippingStates = values.customer.country
          ? globalOptions.shipping_countries
              .find((i) => i.country.code === values.customer.country)
              .states.map((i) => ({ label: i.name, value: i.short }))
          : [];

        // Use custom hook for form population
        useCustomerFormPopulation(isAuthenticated, setFieldValue);

        return (
          <Form>
            {!isAuthenticated && (
              <Box mb={2}>
                <Alert severity="info">
                  Checking out as a guest - for faster checkout login with your
                  email on the <Link to="/profile">Profile</Link> page.
                </Alert>
              </Box>
            )}
            <Grid container spacing={3} justifyContent="center">
              {(!isAuthenticated || !isProfileComplete()) && (
                <Grid item {...GRID_SIZES.CUSTOMER_INFO}>
                  <Typography variant="h6">Customer Info</Typography>
                  <CustomerInfoForm
                    isPickup={isPickup}
                    countries={shipping_countries}
                    shippingStates={shippingStates}
                  />
                </Grid>
              )}
              {isDropship && (
                <Grid item {...GRID_SIZES.DROPSHIP}>
                  <DropshipAddressForm countries={shipping_countries} />
                </Grid>
              )}
            </Grid>
            <Box maxWidth={270} margin="auto">
              <Button
                variant="outlined"
                color="primary"
                component={Link}
                to="/checkout/shipping"
              >
                Back
              </Button>
              <Button
                className={classes.btnxl}
                size="large"
                variant="contained"
                color="secondary"
                onClick={handleSubmit}
              >
                Continue Checkout
              </Button>
            </Box>
          </Form>
        );
      }}
    </Formik>
  );
};

const Address = ({ history }) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const cart = useSelector((s) => s.cart);
  const { globalOptions } = useSelector((s) => s.lab);
  const { isAuthenticated, isProfileComplete } = useAuth();

  const onSubmit = (values) => {
    //localStorage.setItem('customerDetails', JSON.stringify(values.customer));
    dispatch(updateCustomer(values.customer));
    if (values.shippingAddress) {
      dispatch(updateAddress(values.shippingAddress));
    }
    history.push('/checkout/pay');
  };

  if (!cart.shippingMethod) {
    return <Redirect to="/checkout" />;
  }

  const isDropship = cart.shippingMethod.shiptype == 'dropship';
  const isPickup = cart.shippingMethod.shiptype === 'pickup';
  const { shipping_countries } = globalOptions;
  const { customer, shippingAddress } = cart;

  return (
    <Container>
      <Box mt={4} mb={4}>
        <AddressForm
          isAuthenticated={isAuthenticated}
          isDropship={isDropship}
          isPickup={isPickup}
          shipping_countries={shipping_countries}
          onSubmit={onSubmit}
          shippingAddress={shippingAddress}
        />
      </Box>
    </Container>
  );
};

export default Address;
